import { useMemo } from 'react';
import { buildActiveCarePlan } from '../../utils/activeCarePlan';
import { activeReviewProgress, applyTimelineReview } from '../../utils/timelineReview';
import '../../styles/editcase/ActiveCarePlanCard.css';

/**
 * Derived card showing the patient's currently-active care plan, built from
 * the review-applied timeline (dismissed events excluded, corrected
 * statuses honored). With review handlers it doubles as the review surface
 * for the active subset that gates the case.
 *
 * @param {Object}   props
 * @param {Array}    props.events  – Raw timeline events from the case doc.
 * @param {Object}   props.review  – timeline_review map keyed by event id.
 * @param {function} props.onReview – (eventId, decision, correctedStatus);
 *                                    omit for a read-only card.
 * @param {function} props.onConfirmAll – bulk-confirm for unreviewed active
 *                                    events; omit to hide the button.
 */
export default function ActiveCarePlanCard({ events, review, onReview, onConfirmAll }) {
    const reviewedEvents = useMemo(
        () => applyTimelineReview(events || [], review || {}),
        [events, review],
    );
    const groups = useMemo(() => buildActiveCarePlan(reviewedEvents), [reviewedEvents]);
    const progress = useMemo(
        () => activeReviewProgress(events || [], review || {}),
        [events, review],
    );

    if (groups.length === 0) {
        return (
            <div className="active-care-plan-card active-care-plan-card--empty">
                <h3 className="active-care-plan-card-title">Active Care Plan</h3>
                <p className="active-care-plan-card-empty">
                    No events currently marked as active. Review the timeline for details.
                </p>
            </div>
        );
    }

    const activeEvents = groups.flatMap((g) => g.events);
    const unconfirmed = activeEvents.filter((e) => !(review || {})[e.id]?.decision);

    return (
        <div className="active-care-plan-card">
            <h3 className="active-care-plan-card-title">Active Care Plan</h3>
            <p className="active-care-plan-card-hint">
                Currently-active treatments, diagnoses, and orders, ready for handoff.
            </p>
            {onReview && progress.total > 0 && (
                <div className="active-care-plan-review-bar">
                    <span className="active-care-plan-review-progress">
                        {progress.reviewed} / {progress.total} active events reviewed
                    </span>
                    {onConfirmAll && unconfirmed.length > 0 && (
                        <button
                            type="button"
                            className="timeline-review-button"
                            onClick={() => onConfirmAll(activeEvents)}
                        >
                            Confirm all active
                        </button>
                    )}
                </div>
            )}
            <div className="active-care-plan-groups">
                {groups.map((group) => (
                    <section key={group.category} className="active-care-plan-group">
                        <h4 className="active-care-plan-group-label">{group.label}</h4>
                        <ul className="active-care-plan-event-list">
                            {group.events.map((event) => {
                                const entry = (review || {})[event.id] || {};
                                return (
                                    <li key={event.id} className="active-care-plan-event">
                                        <span className="active-care-plan-event-title">{event.title}</span>
                                        {event.description && (
                                            <span className="active-care-plan-event-description">
                                                {' — '}
                                                {event.description}
                                            </span>
                                        )}
                                        {onReview && (
                                            <span className="active-care-plan-event-review">
                                                <button
                                                    type="button"
                                                    className={
                                                        'timeline-review-button' +
                                                        (entry.decision === 'confirmed'
                                                            ? ' timeline-review-button--confirmed'
                                                            : '')
                                                    }
                                                    onClick={() => onReview(
                                                        event.id,
                                                        entry.decision === 'confirmed' ? null : 'confirmed',
                                                        entry.decision === 'confirmed'
                                                            ? null
                                                            : (entry.corrected_status ?? null),
                                                    )}
                                                >
                                                    {entry.decision === 'confirmed' ? '✓' : 'Confirm'}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="timeline-review-button timeline-review-button--dismiss"
                                                    onClick={() => onReview(event.id, 'dismissed')}
                                                >
                                                    Dismiss
                                                </button>
                                            </span>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    </section>
                ))}
            </div>
        </div>
    );
}
